import React, { JSX, useCallback, useState, useMemo } from 'react';
import { Platform, Pressable, Text, View, StyleSheet, useWindowDimensions, PixelRatio } from 'react-native';
import styles from '../../../stylesheet';
import * as Animatable from 'react-native-animatable';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useDispatch, useSelector } from 'react-redux';
import { MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { CountUp } from 'use-count-up';
import { SizableText, Card, XStack, YStack } from 'tamagui';
import { useDeviceType } from '@hooks/useDeviceType';

type MenuIconProps = {
  cardTitle?: String | JSX.Element,
  children: JSX.Element[] | JSX.Element,
  theme?: boolean,
  props?: any,
  keyString?: string,
  style?: any,
  green?: boolean,
  fillAll?: boolean,
  subtitle?: string,
  smallCard?: boolean,
  newFeature?: boolean,
  beta?: boolean,
  new?: boolean,
  pressFunction: Function,
  count?: number,
  minWidth?: number,
  comingSoon?: boolean
}

const MenuIconCard: React.FC<MenuIconProps> = ({ count = undefined, smallCard = false, cardTitle, newFeature, comingSoon = false, beta = false, new: isNew = false, subtitle, children, theme, keyString, green, fillAll, pressFunction, minWidth, ...props }) => {
  const appTheme = themeSelector();
  const { isTablet } = useDeviceType();
  const { width } = useWindowDimensions();

  const handlePress = useCallback(() => {
    pressFunction();
  }, [pressFunction]);

  // Detect lower DPI devices
  const pixelRatio = PixelRatio.get();
  const isLowerDPI = useMemo(() => {
    // Lower DPI devices typically have pixel ratio < 2, or smaller screens
    return pixelRatio < 2 || width < 480;
  }, [pixelRatio, width]);

  // Responsive variables
  const isSmallScreen = width < 600;
  const isMediumScreen = width >= 600 && width < 1024;
  const showCompactMode = isSmallScreen || smallCard;

  // Text size multipliers - reduce more aggressively for lower DPI
  const textScale = useMemo(() => {
    if (isLowerDPI && showCompactMode) return 0.85; // Very small screens with low DPI
    if (isLowerDPI) return 0.9; // Low DPI devices
    if (showCompactMode) return 0.95; // Small screens but normal DPI
    return 1; // Normal size
  }, [isLowerDPI, showCompactMode]);

  return (
    <Animatable.View
      key={keyString}
      animation="fadeInUp"
      duration={600}
      style={{
        gap: 5,
        minWidth: minWidth ? minWidth : Platform.OS === 'web' ? 300 : (isSmallScreen ? "100%" : (isTablet ? "40%" : 400)),
        width: '100%',
        alignSelf: 'flex-start',
        minHeight: showCompactMode ? 100 : 120,
        flex: 1,
        flexDirection: 'row',
        ...props.style
      }}
    >
   

      {/* NEW Badge */}
      {(newFeature || isNew || comingSoon) && (
        <Animatable.View
          animation="pulse"
          iterationCount="infinite"
          duration={2000}
          style={{
            position: 'absolute',
            left: beta ? 50 : -6,
            backgroundColor: '#FF4444',
            borderRadius: 10,
            paddingHorizontal: 8,
            paddingVertical: 3,
            minWidth: 40,
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#FF4444',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.6,
            shadowRadius: 4,
            elevation: 6,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.3)',
            zIndex: 10,
          }}
        >
          <Text style={{
            color: 'white',
            fontSize: Math.round(10 * textScale),
            fontWeight: 'bold',
            textAlign: 'center',
            letterSpacing: 0.5,
          }}>
            {comingSoon ? 'COMING SOON' : 'NEW'}
          </Text>
        </Animatable.View>
      )}

      {/* BETA Badge */}
      {beta && (
        <Animatable.View
          animation="pulse"
          iterationCount="infinite"
          duration={2000}
          style={{
            position: 'absolute',
            left: -6,
            backgroundColor: '#FF8C00',
            borderRadius: 10,
            paddingHorizontal: 8,
            paddingVertical: 3,
            minWidth: 45,
            alignItems: 'center',
            justifyContent: 'center',
            shadowColor: '#FF8C00',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.6,
            shadowRadius: 4,
            elevation: 6,
            borderWidth: 1,
            borderColor: 'rgba(255, 255, 255, 0.3)',
            zIndex: 10,
          }}
        >
          <Text style={{
            color: 'white',
            fontSize: Math.round(10 * textScale),
            fontWeight: 'bold',
            textAlign: 'center',
            letterSpacing: 0.5,
          }}>
            BETA
          </Text>
        </Animatable.View>
      )}

      {/* Main Card */}
      <Pressable
        onPress={handlePress}
        style={{ flex: 6,  flexDirection: 'row', alignSelf: 'stretch' }}
      >
        {({ hovered }) => (
          <Card
            style={[
              styles.modernCard,
              {
                flex: 1,
                minWidth: fillAll ? '100%' : '100%',
                backgroundColor: hovered
                  ? (appTheme ? '#f8fafc' : '#334155')
                  : (appTheme ? 'white' : '#1e293b'),
                borderColor: appTheme ? '#e2e8f0' : '#334155',
                borderWidth: 1,
                opacity: comingSoon ? 0.5 : 1,
                transform: hovered ? [{ scale: 1.02 }] : [{ scale: 1 }],
                shadowColor: appTheme ? '#000' : '#fff',
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.1,
                shadowRadius: 8,
                elevation: 5,
              }
            ]}
          >
            <XStack alignItems="center" padding={showCompactMode ? 12 : 16} gap={12}>
              {/* Count Badge - Web Only */}
              {count && count > 0 && Platform.OS === 'web' && (
                <Animatable.View
                  animation="slideInLeft"
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 1,
                    maxHeight: "80%",
                    padding: 6,
                    minWidth: "15%",
                    left: -25,
                    backgroundColor: appTheme ? '#3b82f6' : '#1d4ed8',
                    borderRadius: 12,
                    marginRight: 8,
                  }}
                >
                  <Text
                    numberOfLines={1}
                    allowFontScaling={false}
                    adjustsFontSizeToFit
                    style={{
                      fontSize: Math.round((showCompactMode ? 24 : 32) * textScale),
                      fontWeight: '700',
                      color: "white",
                      textAlign: 'center',
                    }}
                  >
                    <CountUp isCounting end={count} duration={2} />
                  </Text>
                </Animatable.View>
              )}
              {/* Icon Section */}
              {children && (
                <View
                  style={{
                    width: showCompactMode ? 50 : 60,
                    height: showCompactMode ? 50 : 60,
                    marginRight: 20,
                    borderRadius: showCompactMode ? 25 : 30,
                    backgroundColor: green
                      ? (appTheme ? '#dcfce7' : '#064e3b')
                      : (appTheme ? '#dbeafe' : '#1e3a8a'),
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                  }}
                >
                  {/* Background Circle Effect */}
                  <View
                    style={{
                      position: 'absolute',
                      width: '150%',
                      height: '150%',
                      marginRight: "5%",
                      borderRadius: 50,
                      backgroundColor: green
                        ? (appTheme ? '#10b981' : '#34d399')
                        : (appTheme ? '#3b82f6' : '#60a5fa'),
                      opacity: 0.1,
                    }}
                  />

                  {/* Icon Container */}
                  <View style={{ zIndex: 1 }}>
                    {React.cloneElement(children as React.ReactElement, { style: { ...(children as React.ReactElement).props?.style, minHeight: 80, minWidth: 80 } })}
                  </View>

                  {/* Mobile Count Badge */}
                  {Platform.OS !== 'web' && count && count > 0 && (
                    <View
                      style={{
                        position: 'absolute',
                        top: -8,
                        right: -8,
                        backgroundColor: '#ef4444',
                        borderRadius: 10,
                        paddingHorizontal: 6,
                        paddingVertical: 2,
                        minWidth: 20,
                        alignItems: 'center',
                      }}
                    >
                      <Text
                        style={{
                          fontSize: Math.round(10 * textScale),
                          fontWeight: 'bold',
                          color: "white"
                        }}
                      >
                        <CountUp isCounting end={count} duration={2} />
                      </Text>
                    </View>
                  )}
                </View>
              )}

              {/* Content Section */}
              <YStack flex={1} gap={4}>
                <Text
                  numberOfLines={2}
                  adjustsFontSizeToFit
                  allowFontScaling={true}
                  style={{
                    fontSize: Math.round((showCompactMode ? 14 : 16) * textScale),
                    fontWeight: "600",
                    color: appTheme ? "#1e293b" : "#f1f5f9",
                    lineHeight: Math.round((showCompactMode ? 18 : 20) * textScale),
                  }}
                >
                  {cardTitle}
                </Text>

                {subtitle && (
                  <SizableText
                    fontSize={Math.round((showCompactMode ? 11 : 12) * textScale)}
                    color={appTheme ? "#64748b" : "#94a3b8"}
                    fontStyle="italic"
                    numberOfLines={2}
                  >
                    {subtitle}
                  </SizableText>
                )}
              </YStack>

              {/* Arrow Icon */}
              <View
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 16,
                  backgroundColor: appTheme ? '#f1f5f9' : '#334155',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <MaterialIcons
                  name="arrow-forward-ios"
                  size={16}
                  color={appTheme ? "#64748b" : "#94a3b8"}
                />
              </View>
            </XStack>
          </Card>
        )}
      </Pressable>
    </Animatable.View>
  )
}



export default MenuIconCard;